import { APP_CONFIG } from "config/appConfig";
import {
    HttpStatus,
    SecondsToCountdown,
    SEND_VERIFY_CODE_SUCCESS_MESSAGE,
} from "constant";
import { AppContext } from "context/appContext";
import { useContext, useEffect, useRef, useState } from "react";
import { Container, Row } from "react-bootstrap";
import { useHistory, useLocation } from "react-router";
import AccountService from "service/accountService";
import keycloakService from "service/keycloakService";
import authService, {
    AuthMFAResponse,
    LoginSession,
    TokenResponse,
} from "service/authenticationService";
import AxiosInstance from "service/axiosInstance";
import { storeUserAction } from "store/actions";
import { showErrorAlert, showSuccessAlert } from "utils/alert";
import { getUserOrgs, prepareApp } from "utils/authentication";
import { isHttpSuccess } from "utils/functions";
import EnterMFACode from "../components/authentication/EnterMFACode";
import LeftContent from "../components/authentication/LeftContent";
import LoginForm from "../components/authentication/LoginForm";
import Spinner from "../components/common/Spinner";
import { useAuth } from "hooks/useAuth";
import { useAuthStore } from "store/useAuthStore";

const Home = () => {
    const { storeDispatchActions } = useContext(AppContext);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isError, setIsError] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoginWithPhoneNumber, setIsLoginWithPhoneNumber] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [isMFAEnable, setIsMFAEnable] = useState(false);
    const [session, setSession] = useState<LoginSession>();
    const [countdown, setCountdown] = useState(SecondsToCountdown);
    const [isAuthCheckComplete, setIsAuthCheckComplete] = useState(false);
    const location: any = useLocation();
    const history: any = useHistory();
    const { signOut } = useAuth();

    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const setTokens = useAuthStore((state) => state.setTokens);
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {

        const getUserEmailForLogin = async () => {
            // When trigger this function, 
            // we are assume there is token exist in the store(don't know it's valid or not. axiosInstance will handle if expired)

            let userEmail = email;
            let shouldRedirectToSignUp = false;

            if (APP_CONFIG.APP_IS_ONPREM) {

                // Check if user is already exist in IOTP
                const userResponse = await AccountService.getAccountInfo();


                if (userResponse.status === HttpStatus.FORBIDDEN) {
                    // User is not exist in IOTP, check if user is exist in Keycloak
                    const userProfile = await keycloakService.getUserProfile();

                    // Missing keycloak user means invalid/expired on-prem session.
                    // So call the signOut to clear the session and redirect to login page
                    if (!userProfile) {
                        await signOut();
                        return { userEmail, shouldRedirectToSignUp: false };
                    }

                    // User is exist in Keycloak, redirect to sign up page to create user in IOTP
                    history.push({
                        pathname: "/sign-up",
                        state: { fromHome: true },
                    });
                    shouldRedirectToSignUp = true;
                } else if (isHttpSuccess(userResponse.status)) {
                    // User is already exist in IOTP
                    userEmail = userResponse.data.email;
                } else {
                    throw new Error(
                        `Failed to load account info: ${userResponse.status}`
                    );
                }
            }

            return { userEmail, shouldRedirectToSignUp };
        };

        const initialChecking = async () => {
            if (!isAuthenticated) {
                // Check if component is still mounted before any state update
                if (!isMountedRef.current) return;

                // Not authenticated: stop checking and show login form
                setIsAuthCheckComplete(true);
                setIsLoading(false);
                return;
            }

            if (isLoggedIn || isLoading) {
                return;
            }

            // At this point we know the user is authenticated
            // Check if component is still mounted before any state update
            if (!isMountedRef.current) return;
            setIsLoading(true);

            try {
                const { userEmail, shouldRedirectToSignUp } =
                    await getUserEmailForLogin();

                if (shouldRedirectToSignUp) {
                    return;
                }

                // Don't wait for prepareApp - route immediately
                // prepareApp will be called in Dashboard or can be called in background
                setIsLoggedIn(true);

                await prepareApp(
                    storeDispatchActions,
                    userEmail,
                    () => {
                        globalThis.window.loadAuthorizationConfig();
                    }
                );

                history.push("/dashboard");
            } catch (error) {
                console.error("Post-login flow error:", error);
                if (!isMountedRef.current) return;
                setIsLoading(false);
                setIsAuthCheckComplete(true);
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        };

        initialChecking();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthCheckComplete) return;

        // This is a safe guard to prevent the app from getting stuck in a loading state
        const timeoutId = globalThis.window.setTimeout(() => {
            if (!isMountedRef.current) return;

            // Fallback: if auth check takes too long, show login form
            setIsAuthCheckComplete(true);
            setIsLoading(false);
        }, 5000);

        return () => {
            globalThis.window.clearTimeout(timeoutId);
        };
    }, [isAuthCheckComplete]);

    useEffect(() => {
        if (
            globalThis.location.pathname ===
                `${APP_CONFIG.APP_PUBLIC_URL}/callback` &&
            globalThis.location.search === "?session=expired"
        ) {
            showErrorAlert({
                title: "Session expired!",
                message: "Your session has expired. Please login again.",
            });
            history.replace("/");
        }
    }, [history]);

    const loginValidation = () => {
        let isValid = true;

        if (!password.trim() || !email.trim()) isValid = false;

        return isValid;
    };

    const handleLogin = async (resend?: any) => {
        if (!loginValidation()) return;

        setIsLoading(true);
        try {
            let _email = email.toLocaleLowerCase();
            const response = await authService.login({
                email: _email,
                password: password,
            });
            const { status, data, error } = response;

            if (!isHttpSuccess(status)) {
                setIsLoading(false);
                setIsError(true);

                if (error?.code === "NetworkError") {
                    showErrorAlert({
                        title: "No internet connection",
                        message:
                            "Please check your internet connection and try again.",
                    });
                    return;
                }

                if (
                    status === HttpStatus.BAD_REQUEST &&
                    error?.message === "Password attempts exceeded"
                ) {
                    setErrorMessage(
                        "You've exceeded the maximum number of password attempts. Please try again after some time."
                    );
                    return;
                }

                setErrorMessage("Incorrect username or password.");
                return;
            }

            const { ChallengeName, Session } = data as AuthMFAResponse;
            if (ChallengeName === "SMS_MFA") {
                if (resend) {
                    showSuccessAlert({
                        title: `New Code Sent`,
                        message: SEND_VERIFY_CODE_SUCCESS_MESSAGE,
                    });
                }
                setIsLoading(false);
                setIsError(false);
                setErrorMessage("");
                setEmail(_email);
                setSession(Session);
                setCountdown(SecondsToCountdown);
                setIsMFAEnable(true);
                return;
            }

            const tokenResponse = data as TokenResponse;
            const tokens = {
                AccessToken: tokenResponse.AccessToken,
                RefreshToken: tokenResponse.RefreshToken,
                IdToken: tokenResponse.IdToken,
            };

            AxiosInstance.defaults.headers.common[
                "Authorization"
            ] = `Bearer ${tokenResponse.AccessToken}`;

            if (isLoginWithPhoneNumber) {
                const userRes = await AccountService.getAccountInfo();

                if (isHttpSuccess(userRes.status)) {
                    _email = userRes.data.email;
                    storeDispatchActions(
                        storeUserAction({
                            userData: userRes.data,
                            isLoaded: true,
                        })
                    );
                } else {
                    throw new Error(`${userRes.status}`);
                }
            }

            setTokens(tokens);

            const getOrgCallback = () => {
                globalThis.window.loadAuthorizationConfig();
                if (
                    location.state?.referer?.pathname === "/billing-history" &&
                    location.state.referer.search &&
                    !location.state.referer.search.includes("callback")
                ) {
                    history.push(
                        `/billing-history?callback&${location.state.referer.search.slice(
                            1
                        )}`
                    );
                }
            };

            history.push("/dashboard");

            // Call all the necessary APIs to start the dashboard
            prepareApp(
                storeDispatchActions,
                email,
                getOrgCallback
            );
        } catch {
            setIsLoading(false);
            setIsError(true);

            // Not expect to happen, but handle it based on the old code
            setErrorMessage("Incorrect username or password.");
        }
    };

    const handelSubmitMFACode = async (code: string) => {
        if (!session) return;

        setErrorMessage("");

        // NEW: Using the unified MFA API - works with both Cognito and Local API
        // The legacy method (authService.submitMFACode(session, code)) is still supported
        const { status, data, error } = await authService.submitMFACode({
            session,
            mfaCode: code,
            challengeName: "SMS_MFA", // Optional: will be inferred if not provided
        });

        if (isHttpSuccess(status)) {
            const { AccessToken } = data as TokenResponse;
            AxiosInstance.defaults.headers.common[
                "Authorization"
            ] = `Bearer ${AccessToken}`;

            await AccountService.getAccountInfo();

            getUserOrgs(storeDispatchActions, email, () => {
                const tokenResponse = data as TokenResponse;
                const tokens = {
                    AccessToken: tokenResponse.AccessToken,
                    RefreshToken: tokenResponse.RefreshToken,
                    IdToken: tokenResponse.IdToken,
                };
                setTokens(tokens);
            });
        } else {
            setErrorMessage(
                error?.message || " Invalid OTP code. Please try again."
            );
        }
    };

    const handleBackToLogin = () => {
        setIsError(false);
        setErrorMessage("");
        setIsMFAEnable(false);
    };

    const renderComponent = () => {
        if (isMFAEnable) {
            return (
                <EnterMFACode
                    onSubmit={handelSubmitMFACode}
                    onBack={handleBackToLogin}
                    errorMessage={errorMessage}
                    handleLogin={handleLogin}
                    countdown={countdown}
                    setCountdown={setCountdown}
                />
            );
        }
        return (
            <LoginForm
                handleLogin={handleLogin}
                isLoading={isLoading}
                isLoggedIn={isLoggedIn}
                setEmail={setEmail}
                setPassword={setPassword}
                setIsLoginWithPhoneNumber={setIsLoginWithPhoneNumber}
                isError={isError}
                errorMessage={errorMessage}
            />
        );
    };

    const DesktopComponent = (
        <section>
            <Container fluid>
                <Row className="flex-row-reverse">
                    {renderComponent()}
                    <LeftContent></LeftContent>
                </Row>
            </Container>
        </section>
    );

    if (!isAuthCheckComplete) {
        return <Spinner />;
    }

    return DesktopComponent;
};

export default Home;
